import * as React from 'react';
import {Suspense} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link} from 'react-router-dom';
import {Camera, Plus} from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';
import {FacebookLoginForm} from './AuthMenu';
import {ClientPortalQuery} from './__generated__/ClientPortalQuery.graphql';

const QUERY = graphql`
    query ClientPortalQuery {
        viewer {
            id
            name
            image
            reservations {
                id
                status
                startsAt
                endsAt
                days
                quotedTotal
                lines { id asset { name } }
            }
            engagements {
                id
                interests
                projectName
                status
                createdAt
            }
        }
        facebookLoginEnabled
    }`;

const STATUS: Record<string, { label: string; className: string }> = {
    REQUESTED: {label: 'Awaiting confirmation', className: 'text-amber-300 border-amber-300/40'},
    DRAFT: {label: 'Awaiting confirmation', className: 'text-amber-300 border-amber-300/40'},
    CONFIRMED: {label: 'Confirmed', className: 'text-lumina-accent border-lumina-accent/40'},
    PICKED: {label: 'Ready for pickup', className: 'text-lumina-accent border-lumina-accent/40'},
    CHECKED_OUT: {label: 'Out with you', className: 'text-lumina-purple border-lumina-purple/40'},
    RETURNED: {label: 'Returned', className: 'text-emerald-400 border-emerald-400/40'},
    CANCELLED: {label: 'Cancelled', className: 'text-red-400 border-red-400/40'},
};

const fmt = (iso: string) => new Date(iso).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'});

const Portal: React.FC = () => {
    // store-and-network: shows cached data immediately and refreshes behind it. (network-only
    // caused a refetch loop under the public Navbar with Relay 20 / React 19.)
    const data = useLazyLoadQuery<ClientPortalQuery>(QUERY, {}, {fetchPolicy: 'store-and-network'});

    if (!data.viewer) {
        return (
            <div className="max-w-md mx-auto text-center border border-gray-800 bg-[#050505] p-10">
                <Camera className="w-10 h-10 text-lumina-accent mx-auto mb-4"/>
                <h1 className="text-2xl font-display font-bold mb-2">Your rentals</h1>
                <p className="text-gray-400 text-sm mb-6">Sign in to see your booking requests, confirmations and returns.</p>
                {data.facebookLoginEnabled ? (
                    <FacebookLoginForm className="w-full py-3 bg-[#1877F2] text-white font-bold text-sm tracking-widest uppercase hover:bg-[#1877F2]/80">
                        Continue with Facebook
                    </FacebookLoginForm>
                ) : (
                    <p className="text-gray-500 text-sm">Sign-in isn't available right now.</p>
                )}
            </div>
        );
    }

    const active = data.viewer.reservations.filter((r) => !['RETURNED', 'CANCELLED'].includes(r.status));
    const past = data.viewer.reservations.filter((r) => ['RETURNED', 'CANCELLED'].includes(r.status));

    const Row: React.FC<{ r: typeof active[number] }> = ({r}) => {
        const status = STATUS[r.status] ?? {label: r.status, className: 'text-gray-400 border-gray-700'};
        return (
            <Link to={`/client/reservations/${encodeURIComponent(r.id)}`}
                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-4 py-4 hover:bg-white/[0.03]">
                <span className={`text-[10px] font-bold px-2 py-0.5 border w-fit uppercase tracking-wider ${status.className}`}>{status.label}</span>
                <span className="text-white md:w-64">{fmt(r.startsAt)} → {fmt(r.endsAt)}<span className="text-gray-600"> · {r.days}d</span></span>
                <span className="text-gray-500 text-sm flex-1 truncate">{r.lines.map((l) => l.asset.name).join(', ')}</span>
                {r.quotedTotal && <span className="text-lumina-accent font-display text-sm">${r.quotedTotal}</span>}
            </Link>
        );
    };

    return (
        <div className="max-w-5xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    {data.viewer.image && <img src={data.viewer.image} alt="" className="w-12 h-12 rounded-full border border-gray-700"/>}
                    <div>
                        <span className="text-lumina-purple font-display text-sm tracking-wider uppercase">Client portal</span>
                        <h1 className="text-3xl font-display font-bold">{data.viewer.name}</h1>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Link to="/client/engagements/new" className="px-6 py-3 bg-lumina-accent text-black font-bold text-sm tracking-widest uppercase hover:bg-white flex items-center gap-2 w-fit">
                        <Plus className="w-4 h-4"/> New engagement
                    </Link>
                    <Link to="/book" className="px-6 py-3 bg-lumina-purple text-white font-bold text-sm tracking-widest uppercase hover:bg-lumina-purple/80 flex items-center gap-2 w-fit">
                        <Plus className="w-4 h-4"/> Book gear
                    </Link>
                </div>
            </div>

            <section className="mb-10">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Upcoming & active</h2>
                {active.length === 0 ? (
                    <p className="text-gray-500 text-sm border border-gray-800 p-6">No bookings yet. <Link to="/book" className="text-lumina-accent">Browse the gear →</Link></p>
                ) : (
                    <div className="border border-gray-800 divide-y divide-gray-900">{active.map((r) => <Row key={r.id} r={r}/>)}</div>
                )}
            </section>

            <section className="mb-10">
                <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Engagements</h2>
                {data.viewer.engagements.length === 0 ? (
                    <p className="text-gray-500 text-sm border border-gray-800 p-6">No projects yet. <Link to="/client/engagements/new" className="text-lumina-accent">Start one →</Link></p>
                ) : (
                    <div className="border border-gray-800 divide-y divide-gray-900">
                        {data.viewer.engagements.map((e) => (
                            <Link key={e.id} to={`/client/engagements/${encodeURIComponent(e.id)}`}
                                  className="flex flex-col md:flex-row md:items-center gap-2 md:gap-6 px-4 py-4 hover:bg-white/[0.03]">
                                <span className={`text-[10px] font-bold px-2 py-0.5 border w-fit uppercase tracking-wider ${
                                    e.status === 'closed' ? 'text-gray-500 border-gray-700' : e.status === 'contacted' ? 'text-lumina-accent border-lumina-accent/40' : 'text-amber-300 border-amber-300/40'}`}>
                                    {e.status === 'pending' ? 'Received' : e.status === 'contacted' ? 'In conversation' : 'Closed'}
                                </span>
                                <span className="text-white md:w-64 truncate">{e.projectName ?? 'Untitled project'}</span>
                                <span className="text-gray-500 text-sm flex-1 truncate">{e.interests.map((i) => i.replace(/_/g, ' ')).join(', ')}</span>
                                <span className="text-gray-500 text-xs">{new Date(e.createdAt).toLocaleDateString()}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </section>

            {past.length > 0 && (
                <section>
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-2">Past</h2>
                    <div className="border border-gray-800 divide-y divide-gray-900 opacity-70">{past.map((r) => <Row key={r.id} r={r}/>)}</div>
                </section>
            )}
        </div>
    );
};

const ClientPortal: React.FC = () => (
    <div className="bg-lumina-dark min-h-screen text-white">
        <Navbar/>
        <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
            <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                <Portal/>
            </Suspense>
        </main>
        <Footer/>
    </div>
);

export default ClientPortal;
